<template>
    <ContentOrLoading>
        <ValidationObserver v-if="!loading" tag="form" ref="observer" @submit.prevent="submit()">
            <b-row>
                <b-col cols="12">
                    <FormInput label="Email" type="email" name="email" id="email" rules="required" v-model="localSubscriptor.email" :disabled="loading" />
                </b-col>
                <b-col cols="12">
                    <FormInput label="Nombre" type="text" name="nombre" id="nombre" rules="required" v-model="localSubscriptor.nombre" :disabled="loading" />
                </b-col>
                <b-col cols="12">
                    <FormInput label="Apellidos" type="text" name="apellidos" id="apellidos" rules="required" v-model="localSubscriptor.apellidos" :disabled="loading" />
                </b-col>
                <b-col cols="12" class="mb-4">
                    <label>País</label>
                    <multiselect
                        v-model="localSubscriptor.pais"
                        :options="paises"
                        :searchable="true"
                        :close-on-select="true"
                        :clear-on-select="false"
                        :preserve-search="true"
                        placeholder="Selecciona un país"
                        :disabled="loading"
                        label="text"
                        track-by="value"
                        :loading="loadingPaises"
                        @input="onPaisChanged($event)"
                    >
                        <template slot="noOptions">
                            Selecciona un país
                        </template>
                    </multiselect>
                </b-col>
                <b-col cols="12" class="mb-4">
                    <label>Provincia</label>
                    <multiselect
                        v-model="localSubscriptor.provincia"
                        :options="provincias"
                        :searchable="true"
                        :close-on-select="true"
                        :clear-on-select="false"
                        :preserve-search="true"
                        placeholder="Selecciona una provincia"
                        label="text"
                        track-by="value"
                        :loading="loadingProvincias"
                        :disabled="loading || !subscriptor.pais_id"
                        @input="onProvinciaChanged($event)"
                    >
                        <template slot="noOptions">
                            {{ !subscriptor.pais_id ? 'Selecciona primero un país' : 'No hay provincias disponibles' }}
                        </template>
                    </multiselect>
                </b-col>
                <b-col cols="12" class="mb-4">
                    <label>Localidad</label>
                    <multiselect
                        v-model="localSubscriptor.localidad"
                        :options="localidades"
                        :searchable="true"
                        :close-on-select="true"
                        :clear-on-select="false"
                        :preserve-search="true"
                        placeholder="Selecciona una localidad"
                        label="text"
                        track-by="value"
                        :loading="loadingLocalidades"
                        :disabled="loading || !subscriptor.provincia_id"
                    >
                        <template slot="noOptions">
                            {{ !subscriptor.provincia_id ? 'Selecciona primero una provincia' : 'No hay localidades disponibles' }}
                        </template>
                    </multiselect>
                </b-col>
                <b-col cols="12">
                    <div class="form-group mb-4">
                        <label for="telefono">Teléfono</label>
                        <PhoneInput
                            name="telefono"
                            :phone="localSubscriptor.telefono ?? null"
                            :desactivar="loading"
                            :required="false"
                            @setNumber="(number) => localSubscriptor.telefono = number"
                            @validNumber="(valid) => isValidNumber = valid"
                        />
                    </div>
                </b-col>
                <b-col cols="12">
                    <div class="form-group">
                        <label class="text-left">Grupos</label>
                        <ValidationProvider name="grupos" id="grupos" rules="" v-slot="{ errors }">
                            <multiselect
                                v-model="localSubscriptor.grupos"
                                id="grupos"
                                placeholder="Grupos"
                                label="nombre"
                                track-by="value"
                                :class="{ 'disabled': loading }"
                                :multiple="true"
                                :options="grupos"
                            />
                            <span v-if="errors[0]" class="d-block invalid-feedback text-xs bg-danger white h7 mb-4 p-2 font-weight-bold">
                                {{ errors[0] }}
                            </span>
                        </ValidationProvider>
                    </div>
                </b-col>
                <b-col cols="12">
                    <div class="form-group">
                        <label class="text-left">Reputación</label>
                        <div class="d-flex align-items-center" style="gap: 0.5rem;">
                            <div id="rating-tooltip">
                                <star-rating
                                    v-model="reputacionNumero"
                                    :increment="0.5"
                                    :star-size="32"
                                    active-color="#444444"
                                    inactive-color="#d3d3d3"
                                    :show-rating="false"
                                    @current-rating="currentRating = $event"
                                />
                            </div>
                            <b-tooltip target="rating-tooltip" triggers="hover">
                                {{ currentRating }}
                            </b-tooltip>
                        </div>
                    </div>
                </b-col>
                <b-col cols="12">
                    <div class="form-group">
                        <label class="text-left">Activo</label>
                        <b-form-checkbox v-model="localSubscriptor.activo" :disabled="loading" />
                    </div>
                </b-col>
                <b-col cols="12" class="text-right mt-3">
                    <Button type="submit" :disabled="loading">Continuar</Button>
                </b-col>
            </b-row>
        </ValidationObserver>
    </ContentOrLoading>
</template>

<script>
    import apiService from '@/services/api-comunicados';
    import PhoneInput from '@/components/common/PhoneInput';
    import Multiselect from 'vue-multiselect';
    import StarRating from 'vue-star-rating';

    export default {
        props: {
            subscriptor: {
                type: Object,
                required: true
            },
            loading: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                grupos: [],
                paises: [],
                provincias: [],
                localidades: [],
                isValidNumber: false,
                loadingPaises: false,
                loadingProvincias: false,
                loadingLocalidades: false,
                localSubscriptor: {...this.subscriptor},
                currentRating: null
            }
        },
        methods: {
            async submit() {
                const isValid = await this.$refs.observer.validate();
                if(isValid && this.isValidNumber) {
                    if (this.localSubscriptor.grupos) {
                        this.localSubscriptor.grupos_ids = this.localSubscriptor.grupos.map(g => g.id);
                    }

                    this.localSubscriptor.localidad_id = this.localSubscriptor.localidad?.value ?? null;

                    this.$emit('update:subscriptor', this.localSubscriptor);
                    this.$emit('terminado');
                }
            },
            async getGrupos() {
                this.$emit('update:loading', true);

                try {
                    const response = await apiService.getGrupos( { to_select: true });
                    this.grupos = response.data;
                } catch (error) {
                    console.error('Error al obtener los subscriptores', error);
                } finally {
                    this.$emit('update:loading', false);
                }
            },

            async getPaisesForm(){
                this.loadingPaises = true;
                try {
                    const response = await apiService.getPaises();
                    this.paises = response.data.map(pais => ({value: pais.id, text: pais.nombre}));
                } catch (error) {
                    this.$toast.error("Error al cargar países");
                } finally {
                    this.loadingPaises = false;
                }
            },

            async onPaisChanged(pais){
                this.localSubscriptor.pais_id = pais?.value ?? null;

                if (!this.localSubscriptor.pais_id) {
                    this.provincias = [];
                    this.localidades = [];
                    return;
                }

                this.loadingProvincias = true;
                try{
                    const response = await apiService.getProvincias(this.localSubscriptor.pais_id);
                    this.provincias = response.data.map(provincia => ({value: provincia.id, text: provincia.nombre}));
                }
                catch(error){
                    this.$toast.error("Error al cargar provincias");
                } finally {
                    this.loadingProvincias = false;
                }
            },

            async onProvinciaChanged(provincia){
                this.localSubscriptor.provincia_id = provincia?.value ?? null;

                if(!this.localSubscriptor.provincia_id) {
                    this.localidades = [];
                    return;
                }

                this.loadingLocalidades = true;
                try {
                    const response = await apiService.getLocalidades(this.subscriptor.pais_id, this.subscriptor.provincia_id);
                    this.localidades = response.data.map(localidad => ({value: localidad.id, text: localidad.nombre}));
                } catch (error) {
                    this.$toast.error("Error al cargar localidades");
                } finally {
                    this.loadingLocalidades = false;
                }
            }
        },
        computed: {
            reputacionNumero: {
                get() {
                    return parseFloat(this.localSubscriptor.reputacion) || 0;
                },
                set(value) {
                    this.localSubscriptor.reputacion = value;
                }
            }
        },
        created() {
            this.getGrupos();
            this.getPaisesForm();
        },
        components: {
            PhoneInput, Multiselect, StarRating
        },
    }
</script>
<style>
    .multiselect__spinner {
        background: transparent !important;
        top: 0px !important;
        right: 1% !important;
    }
    .multiselect__spinner:after, .multiselect__spinner:before {
        left: -10%;
        width: 25px;
        height: 25px;
        border-radius: 100%;
        border-color: #007BFF transparent transparent;

    }
</style>
